import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Buttons,
  FiltersWrapper,
  HeaderInputButton,
  HeaderWrapper,
  SelectStoresPage,
  StyledHeader,
  StyledSubHeader,
  StyledSubTitle,
  TableRowWrapper,
  TableWrapper,
  TreeViewRowWrapper,
} from './SelectStores.styles';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import isEqual from 'react-fast-compare';
import { useNavigate } from 'react-router-dom';
import PublishMenusModal from '../PublishMenusModal/PublishMenusModal';
import { RowSelectedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTheme } from '@emotion/react';
import Header from '../../../common/Header/Header';
import { useMenuChannels, usePublishMenu } from '../../../services/extract/extract.service';
import { Organization } from '../../../services/menu/menu.types';
import { useStoreMarketsOrganizations } from '../../../services/store/store.service';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import { BuildRequestBodyParams, IndexableDataStructureWrapper } from '../../../components/MenuTreeView/MenuTreeView.types';
import useTreeViewRowStates from '../../../components/MenuTreeView/useMenuTreeViewRowStates';
import { EditCell } from '../../../components/Table/EditCellRenderer/EditCellRenderer.styles';
import { DeleteButton } from '../../Menus/ChangeSpecs/SelectSpec/SelectSpec.styles';
import { PublishMenusRequestBody } from '../../../services/extract/extract.types';
import { showToast } from '../../../components/Toast/Toast';
import { genericErrorShowToastOptions } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { StyledFooter, StyledFooterText, StyledHeaderTitle } from '../../../components/Table/Table.styles';
import Radio from '../../../components/Radio/Radio';
import InputButton from '../../../components/InputButton/InputButton';
import Checkbox from '../../../components/Checkbox/Checkbox';
import MenuTreeView from '../../../components/MenuTreeView/MenuTreeView';
import Table from '../../../components/Table/Table';
import Button from '../../../components/Button/Button';

const SelectStores: FC = () => {
  const { data: menuChannelsOptions } = useMenuChannels(true);
  const [radioFilter, setRadioFilter] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [searchErrorMessage, setSearchErrorMessage] = useState<string | undefined>(undefined);
  const [filterId, setFilterId] = useState<string>('');
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [inputError, setInputError] = useState<boolean>(false);
  const filterRef = useRef<HTMLInputElement>(null);
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const [selectedStores, setSelectedStores] = useState<Organization[]>([]);
  const [tableUpdate, setTableUpdate] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const menuChannels = useMemo(() => menuChannelsOptions?.data, [menuChannelsOptions?.data]);
  const theme = useTheme();
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['ECOMM']);


  const { data: organizations, isError: organizationsError, isLoading } = useStoreMarketsOrganizations(true);
  const selectedGridRef = useRef<AgGridReact>(null);

  const navigate = useNavigate();
  const { mutateAsync: publishMenu, isLoading: isPublishing } = usePublishMenu();

  const translateFilter: any = {
    marketNo: 'Market',
    storeNo: 'Store',
  };
  const translateForTree: any = {
    marketNo: 'org',
    storeNo: 'store',
  };

  useEffect(() => {
    let count = 0;
    if (organizations?.data !== undefined && organizations.data.length > 0) {
      organizations.data.forEach((org) => {
        if (org.children !== undefined) {
          org.children.forEach((store) => {
            count++;
          });
        }
      });
    }
    setDisplayedRowCount(count);
  }, [organizations]);

  useFetchErrorToast([
    { isError: organizationsError, label: 'Error fetching organizations.', id: 'organizationStoresFetchError' },
  ]);

  const dataStructure: IndexableDataStructureWrapper = {
    org: {
      idKey: 'org',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.label,
      buildParentArray: (params: Organization) => {
        return [{ level: 0, id: params.id }];
      },
      getIndexByType: () => {
        return 0;
      },
    },
    store: {
      to: () => '',
      idKey: 'store',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.label,
      buildParentArray: (params: Organization) => {
        const parents = [];
        if (params.parents) {
          parents.push({ level: 0, id: params.parents['org'] });
        }
        parents.push({ level: 1, id: params.id });
        return parents;
      },
      getIndexByType: () => {
        return 1;
      },
    },
  };

  const {
    checkedRows,
    expandedRows,
    handleRowClick,
    handleCheckboxClick,
    handleUpwardsDeselectPropagation,
    handleExternalTableDeselectPropagation,
    resetCheckedRows,
  } = useTreeViewRowStates(dataStructure, 'publishMenus', true);

  const handleRadioValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRadioFilter(event.target.value);
    handleClear();
  };
  const RemoveRowData = (params: any) => {
    return (
      <EditCell>
        <DeleteButton data-testid='deleteStoreButton'>
          <DeleteOutlinedIcon
            fontSize='inherit'
            htmlColor={theme.colors.red}
            style={{ cursor: 'pointer', height: '50%' }}
            onClick={() => {
              const updatedStores = selectedStores.filter((store) => store.id !== params.data.id);
              setSelectedStores(updatedStores);
              handleCheckboxClick(params.data, false);
              handleExternalTableDeselectPropagation(params.data);
            }}
          />
        </DeleteButton>
      </EditCell>
    );
  };

  const columnDefs = [
    {
      headerName: 'Market No',
      field: 'additionalProperties.marketNo',
      minWidth: 80,
      maxWidth: 100,
      valueGetter: (params: any) => {
        if (!params.data) return '';
        
        if (params.data.additionalProperties && params.data.additionalProperties.marketNo) {
          return params.data.additionalProperties.marketNo;
        }
        if (params.data.type === 'store' && params.data.parents?.org && organizations?.data) {
          const parentOrg = organizations.data.find((org) => org.id === params.data.parents.org);
          return parentOrg?.additionalProperties?.marketNo || '';
        }
        return '';
      },
    },
    {
      headerName: 'Store No',
      field: 'id',
      minWidth: 80,
      valueFormatter: (params: any) => {
        return params.data.type === 'store' && params.data.id;
      },
    },
    {
      minWidth: 100,
      maxWidth: 100,
      flex: 1,
      field: 'remove',
      headerName: 'Remove',
      resizable: false,
      cellRenderer: RemoveRowData,
      floatingFilter: false,
    },
  ];

  const handleCheckboxClickInternal = (row: Organization, rowChecked: boolean) => {
    if (rowChecked) {
      if (row.type === 'store') {
        // menu row was checked
        setSelectedStores((selectedStore) => {
          const selectedStoresWithCheckedRowAdded = [...selectedStore, row];
          return Array.from(new Set(selectedStoresWithCheckedRowAdded));
        });
      } else {
        traverseTree(row);
      }
    } else {
      // row was just unchecked
      setSelectedStores((selectedStores) => {
        // filter through selectedMenus and remove all the menus that correspond with that row
        return selectedStores.filter((store) => {
          let isRowAndParentEqual;
          if (store.type === row.type) {
            isRowAndParentEqual = isEqual(row.id, store.id) && isEqual(row.parents, store.parents);
          } else {
            isRowAndParentEqual = isEqual(row.id, store.parents ? store.parents[row.type] : undefined);
          }
          return !isRowAndParentEqual;
        });
      });
      handleUpwardsDeselectPropagation(row);
    }
    // call the fn from useTreeViewRowStates to mark the row as checked/unchecked there
    handleCheckboxClick(row, rowChecked);
  };

  const traverseTree = (row: Organization) => {
    if (row.type === 'store') {
      setSelectedStores((selectedStore) => {
        const selectedStoresWithCheckedRowAdded = [...selectedStore, row];
        return Array.from(new Set(selectedStoresWithCheckedRowAdded));
      });
      handleCheckboxClick(row, true);
    } else if (row.children !== undefined) {
      row.children.forEach((childRow) => traverseTree(childRow));
    }
  };

  const handleClear = () => {
    setSearchInputValue('');
    setInputError(false);
    setErrorMessage('');
    setSearchErrorMessage('');
    setSearchValue('');
  };

  const handleSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(undefined);
    setSearchValue(event.target.value);
    setFilterId('');
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
    setSearchErrorMessage('');
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      await handleSearch();
    }
  };

  const handleAddStoreClick = () => {
    if (!searchInputValue) {
      setSearchErrorMessage('Please enter a store number');
      return;
    }

    setSearchErrorMessage(undefined);

    const store = findStore(searchInputValue);

    if (store) {
      if (!selectedStores.some((selectedStore) => selectedStore.id === store.id)) {
        setSelectedStores((prev) => [...prev, store]);
        handleCheckboxClick(store, true);
        const parentOrg = organizations?.data.find((org) => org.children?.some((child) => child.id === store.id));
        if (parentOrg) handleRowClick(parentOrg, true);
      }
      setSearchInputValue('');
    } else {
      setSearchErrorMessage('Store not found');
    }
  };

  const findStore = (storeId: string): Organization | undefined => {
    return organizations?.data.flatMap((org) => org.children ?? []).find((store) => store.id === storeId);
  };

  const handleSelectAll = () => {
    if (selectedStores.length === displayedRowCount && !isLoading) {
      resetCheckedRows();
      setSelectedStores([]);
    } else if (organizations?.data !== undefined) {
      let allStores: Organization[] = [];
      organizations.data.forEach((org) => {
        handleCheckboxClick(org, true);
        if (org.children !== undefined) {
          org.children.forEach((store) => {
            allStores.push(store);
            handleCheckboxClick(store, true);
          });
        }
      });
      setSelectedStores(allStores);
    }
  };

  const handleSearch = () => {
    setErrorMessage(undefined);
    setFilterId(() => '');
    let isRowFound = false;
    if (organizations?.data && organizations.data.length > 0) {
      if (radioFilter === 'marketNo') {
        for (const orgRow of organizations.data) {
          if (orgRow.label && orgRow.label.toLowerCase().startsWith(searchValue.toLowerCase())) {
            setFilterId(orgRow.id);
            handleRowClick(orgRow, true);
            isRowFound = true;
            break;
          }
        }
      } else if (radioFilter === 'storeNo') {
        setFilterId(searchValue);
        for (const orgRow of organizations.data) {
          if (orgRow.children?.some(store => store.id === searchValue)) {
            handleRowClick(orgRow, true);
            findAndProcessSearchResult(searchValue, 'store', orgRow, orgRow);
            isRowFound = true;
            break;
          }
        }
      }
    }

    if (!isRowFound && radioFilter) {
      setErrorMessage(`${translateFilter[radioFilter]} not found`);
    }
  };
  const findAndProcessSearchResult = (
    searchValue: string,
    type: string,
    row: Organization,
    orgRow: Organization
  ): boolean => {
    if (row.type === type && row.id === searchValue) {
      processSearchResult(row, orgRow);
      return true;
    }

    return row.children?.some((child) => findAndProcessSearchResult(searchValue, type, child, orgRow)) ?? false;
  };

  const processSearchResult = (targetRow: Organization, orgRow: Organization) => {
    const processSiblings = (row: Organization) => {
      if (row !== targetRow && targetRow.parents && targetRow.parents[row.type] === row.id) {
        handleRowClick(row, true);
      }
      if (row.children) {
        row.children.forEach(processSiblings);
      }
    };
    if (orgRow.children) {
      orgRow.children.forEach(processSiblings);
    }
  };

  const handlePublishMenus = async () => {    
    const storeIds = selectedStores.map((store) => store.id);
    const allStoresSelected = storeIds.length === displayedRowCount;

    const payload: PublishMenusRequestBody = {
      allStores: allStoresSelected,
      menuChannels: selectedChannels,
      stores: allStoresSelected ? [] : storeIds,
    };

    await publishMenu(payload, {
      onSuccess: () => {
        showToast({
          label: 'Menus published successfully',
          toastType: ToastTypes.Success,
          id: 'successPublishingMenusInOrgs',
        });
        setSelectedStores([]);
        setSelectedChannels([]);
        resetCheckedRows();
        setTableUpdate(prev => !prev);
        navigate('/publish');
      },
      onError: () => {
        showToast({
          ...genericErrorShowToastOptions,
          label: 'Error publishing menus',
          toastType: ToastTypes.Error,
          id: 'errorPublishingMenusInOrgs',
        });
      },
    });
    setSelectedChannels(['ECOMM']);
  };

  const handleSelectedStoresChanged = (event: RowSelectedEvent) => {
    if (event.node.isSelected() === false) {
      const newSelectedMenus = selectedStores.filter((store) => store.id !== event.data.id);
      setSelectedStores(newSelectedMenus);
      handleCheckboxClick(event.data, false);
      handleExternalTableDeselectPropagation(event.data);
      selectedGridRef.current?.api.selectAll();
      setTableUpdate(!tableUpdate);
    }
  };

  return (
    <>
      <Header title='Select Stores' breadcrumb={{ to: '/tables', label: 'All tables' }} />
      <SelectStoresPage>
        <TableRowWrapper>
          <TreeViewRowWrapper>
            <StyledHeader>
              <HeaderWrapper>
                <StyledHeaderTitle>Available Stores</StyledHeaderTitle>
              </HeaderWrapper>
              <FiltersWrapper>
                <StyledSubTitle>Search by:</StyledSubTitle>
                <Radio
                  checked={radioFilter === 'marketNo'}
                  value='marketNo'
                  onChange={handleRadioValueChange}
                  id={'marketNo'}
                  label={'Franchisee'}
                />
                <Radio
                  checked={radioFilter === 'storeNo'}
                  value='storeNo'
                  onChange={handleRadioValueChange}
                  id={'storeNo'}
                  label={'Store'}
                />
                <HeaderInputButton>
                  <InputButton
                    buttonProps={{
                      label: 'Search',
                      onClick: handleSearch,
                      testId: 'SearchButton',
                      disabled: searchValue === '',
                    }}
                    inputProps={{
                      onChange: handleSearchValue,
                      id: 'searchFilters',
                      label: '',
                      value: searchValue,
                      disabled: radioFilter === '',
                      onKeyDown: handleKeyDown,
                      testId: 'FilterInput',
                      ref: filterRef,
                      errorText: errorMessage,
                      maxLength: 20,
                    }}
                  />
                </HeaderInputButton>
              </FiltersWrapper>
            </StyledHeader>
            <StyledSubHeader data-testid='subtitle'>
              <div style={{ marginLeft: '48px' }}>
                <Checkbox
                  type='checkbox'
                  testId='selectAllCheckBox'
                  checked={selectedStores.length > 0 && selectedStores.length === displayedRowCount}
                  id={'select-all'}
                  aria-checked={selectedStores.length > 0 && selectedStores.length === displayedRowCount}
                  readOnly
                  indeterminate={selectedStores.length > 0 && selectedStores.length < displayedRowCount}
                  label={<StyledSubTitle>Organizations</StyledSubTitle>}
                  onClick={handleSelectAll}
                />
              </div>
            </StyledSubHeader>
            <MenuTreeView
              rows={organizations?.data || []}
              dataStructure={dataStructure}
              checkedRows={checkedRows}
              expandOnCheck={false}
              expandedRows={expandedRows}
              filterId={filterId}
              selectedRows={selectedStores}
              onRowClick={handleRowClick}
              onCheckboxClick={handleCheckboxClickInternal}
              defaultHandleCheckboxClick={handleCheckboxClick}
              isLoading={isLoading}
              externalTableUpdate={tableUpdate}
              radioFilter={translateForTree[radioFilter]}
            />
            <StyledFooter>
              <StyledFooterText>
                {`${displayedRowCount === undefined ? 0 : displayedRowCount} ${
                  displayedRowCount === 1 ? 'record' : 'records'
                }`}
              </StyledFooterText>
            </StyledFooter>
          </TreeViewRowWrapper>
          <TableWrapper>
            <Table
              showHeader
              headerTitle='Selected Stores'
              rowData={selectedStores}
              tableUpdate={tableUpdate}
              returnedRecords={selectedStores.length}
              columnDefs={columnDefs}
              onRowSelected={handleSelectedStoresChanged}
              ref={selectedGridRef}
              rowSelection='multiple'
              headerButtons={[
                {
                  buttonProps: {
                    label: 'Add Store',
                    testId: 'AddMenuSearchButton',
                    onClick: handleAddStoreClick,
                  },
                  inputProps: {
                    label: '',
                    'aria-label': `add menu to publish`,
                    id: 'addMenuToExtract',
                    value: searchInputValue,
                    onChange: handleInputChange,
                    errorText: searchErrorMessage,
                    testId: 'searchStoreInput',
                    disabled: inputError ? true : false,
                  },
                },
              ]}
            />
          </TableWrapper>
        </TableRowWrapper>
        <Buttons>
          <Button
            label='Publish'
            disabled={selectedStores.length === 0}
            testId='publishButton'
            onClick={() => setOpenModal(true)}
          />
        </Buttons>
      </SelectStoresPage>
      <PublishMenusModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        isLoading={isPublishing}
        onPublish={() => handlePublishMenus()}
        selectedStores={selectedStores}
        menuChannelsOptions={menuChannelsOptions?.data}
        selectedChannels={selectedChannels || []}
        setSelectedChannels={setSelectedChannels}
        menuChannels={menuChannels || []}
      />
    </>
  );
};

export default SelectStores;
